import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import type { DurationUnitType } from 'dayjs/plugin/duration';
import { referenceField } from '@platform/shared/helpers/compare-fns';
import type { CustomSqlSerializer } from '@platform/shared/rpc';
import type { DateLib, IUniversalDate } from './base-datetime';
import { isDateStr, localeSettings } from './base-datetime';
import { formatTStoDATE } from './date-formatters';
import { captureException } from '@sentry/browser';

/**
 * Форматирует DATE под TIMESTAMP по формату
 * @param date - дата в виде строки по шалону
 * @param template - формат шаблона
 */
const tzOffset = new Date().getTimezoneOffset();

class DateTimeException extends Error {}

export function parseDateToTS(date: null, template?: string): null;
export function parseDateToTS(date: Date | DateStr | string | number, template?: string): number;
export function parseDateToTS(date: Date | DateStr | string | number | null, template?: string): number | null;
export function parseDateToTS(
    date: Date | DateStr | string | number | null,
    template: string = localeSettings.dateFormat
): number | null {
    if (!date) return null;

    if (typeof date === 'number' && !isNaN(date)) return date;

    if (typeof date === 'string' && date.length === 10 && date.includes('-')) {
        template = localeSettings.serverDateFormat;
    }
    const ts = dayjs(date, template).valueOf();
    if (!ts) {
        captureException(
            new DateTimeException(
                `parseDateToTS(${
                    (JSON.stringify(date), template !== undefined ? JSON.stringify(template) : 'undefined')
                }) failed to parse`
            )
        );
    }
    //в милисекундах
    return ts != null && ts > 0 ? ts : null;
}

export function pickEpochDateFromTS(date: Date | number | null) {
    if (date == null) return null;
    return dayjs(date).startOf('day').valueOf();
}

export function getSqlTimestampFn(date: Date | number | null) {
    return date == null ? 'NULL' : `to_timestamp(${pickEpochDateFromTS(date)})`;
}

function getSqlDateFn(date: DateStr | Date | number | null | undefined): CustomSqlSerializer {
    if (date == null) return () => ['NULL', []];
    let d = '';
    if (isDateStr(date)) d = date;
    else d = formatTStoDATE(date, localeSettings.serverDateFormat);
    return (startIndex) => [`to_date($${startIndex}, '${localeSettings.serverDateFormat}')`, [d]];
}

export function getDateStr(date: IUniversalDate | null | undefined): DateStr | null {
    if (!date) return null;
    if (isDateStr(date)) return date;
    return formatTStoDATE(date, localeSettings.serverDateFormat) as DateStr;
}

getSqlDateFn.asString = function (date: Date | number | null) {
    return date == null
        ? 'NULL'
        : `to_date('${formatTStoDATE(date, localeSettings.serverDateFormat)}', '${localeSettings.serverDateFormat}')`;
};

export { getSqlDateFn };

/**
 * Форматирует DATETIME под TIMESTAMP по формату
 * @param date - дата в виде строки по шалону
 * @param template - формат шаблона
 */
export function parseDateTimeToTS(date: null, template?: string): null;
export function parseDateTimeToTS(date: Date | string, template?: string): number;
export function parseDateTimeToTS(
    date: Date | string | null,
    template: string = localeSettings.dateTimeFormat
): number | null {
    if (!date) return null;
    const ts = dayjs(date, template).valueOf(); //в милисекундах
    return ts != null && ts > 0 ? ts : null;
}

export function sortDateField(dataA: string | number | null, dataB: string | number | null) {
    const valA = dataA != null ? (typeof dataA === 'string' ? parseDateToTS(dataA) : dataA) : null;
    const valB = dataB != null ? (typeof dataB === 'string' ? parseDateToTS(dataB) : dataB) : null;
    return referenceField(valA, valB);
}

export function customDateTime<T>(transform: (dateLib: typeof dayjs) => T): T {
    return transform(dayjs);
}

export function customDateTimeToTS(transform: (dateLib: typeof dayjs) => Dayjs): number {
    return transform(dayjs).valueOf();
}

export function formatDuration(duration: number, unit: DurationUnitType): string {
    return dayjs.duration(duration, unit).humanize();
}

// получить сегодняшнюю дату
export function getTodayDate(template?: string, changeDays?: number) {
    return formatTStoDATE(new Date().getTime(), template ?? localeSettings.serverDateFormat, changeDays);
}
// получить дату с первого дня месяца
export function getDateStartOfMonth(date?: string): DateStr {
    return customDateTime((dateLib) => dateLib(date).startOf('month')).format(
        localeSettings.serverDateFormat
    ) as DateStr;
}
export function getDateEndOfMonth(date?: string): DateStr {
    return customDateTime((dateLib) => dateLib(date).endOf('month')).format(localeSettings.serverDateFormat) as DateStr;
}
/** Русская локаль для компонентов выбора даты (q-date, DatePopupContent). */
export const ruLocaleDates = Object.freeze({
    months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
    monthsShort: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
    days: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
    daysShort: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
});

export { localeSettings };
export type { DateLib };
export * from './date-formatters';
export * from './months';
export * from './quarters';
export * from './years';
export * from './time-estimation';
